import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useRevalidator } from 'react-router-dom'
import { isBefore, endOfMonth } from 'date-fns'
import './payment-method-cell.css'

// COMPONENTS
import * as Icons from '@icons'
import PaymentMethodBrand from '@components/atoms/payment-method-brand'
import Spinner from '@components/atoms/spinner'
import Button from '@components/atoms/button'

// UTILITIES
import { createResponseObj, StripeAPI } from '@apis'
import { flatten } from '@utils/string-helper'
import { isNonEmptyString } from '@utils/type-checkers'
import { toNum } from '@utils/number-helper'
import { queryClient } from '@/lib/queryClient'


interface Props extends React.HTMLAttributes<HTMLDivElement> {
    paymentMethod?: CreditCard | ApplePay | GooglePay
    editable?: boolean
    removable?: boolean
    selected?: boolean
    type?: 'default' | 'add' | 'empty'
    onEditClick?: () => void
}

const PaymentMethodCell: FC<Props> = (props) => {
    const { className: classProps, paymentMethod, editable, removable, selected, type = 'default', onEditClick, children, ...divProps } = props
    const className = flatten('payment-method-cell', { selected }, 'flex flex-col items-stretch justify-center')

    const isExpired = useMemo(() => {
        if (paymentMethod && ('expirationMonth' in paymentMethod && isNonEmptyString(paymentMethod.expirationMonth)) && 'expirationYear' in paymentMethod && isNonEmptyString(paymentMethod.expirationYear)) {
            const expirationMonth = toNum(paymentMethod.expirationMonth)
            const expirationYear = 2000 + toNum(paymentMethod.expirationYear)

            const expirationDate = endOfMonth(new Date(expirationYear, expirationMonth - 1))
            return isBefore(expirationDate, new Date())
        } else return false
    }, [paymentMethod])

    const title = <strong style={{ textTransform: 'capitalize' }}>
        {`${paymentMethod?.type === 'credit_card' ? (paymentMethod?.name || paymentMethod?.cardType?.replace('_', ' ')) : paymentMethod?.type.replace('_', ' ')}`}
        {!!paymentMethod?.lastFour ? ` **** ${paymentMethod.lastFour}` : ''}
    </strong>
    const subtitle = (paymentMethod && ('expirationMonth' in paymentMethod && isNonEmptyString(paymentMethod.expirationMonth)) && 'expirationYear' in paymentMethod && isNonEmptyString(paymentMethod.expirationYear))
        ? <span className={flatten('text-body-2', isExpired ? 'expiredDate' : '')}>{`${isExpired ? 'Expired' : 'Expires'} ${paymentMethod.expirationMonth || 'MM'}/${paymentMethod.expirationYear || 'YY'}`}</span>
        : null
    return (
        <div className={flatten(className, isExpired ? 'expired' : '')} data-cell-type={type} {...divProps}>
            {(type !== 'empty') && (
                <div className="flex flex-row items-center justify-between" style={{ gap: 'inherit' }}>
                    {(type === 'default') && (<>
                        <p className="pm-details flex flex-col">
                            {title}
                            {subtitle}
                        </p>
                        <PaymentMethodBrand brand={paymentMethod?.type === 'credit_card' ? paymentMethod?.cardType || paymentMethod?.type : paymentMethod?.type} style={{ height: '2rem' }} />

                        {(!!editable && paymentMethod?.type === 'credit_card' && !!paymentMethod.token) && (<img src="/icons/pencil.svg" onClick={onEditClick} style={{ cursor: 'pointer', pointerEvents: 'auto' }} />)}
                        {(!!removable && paymentMethod?.type === 'credit_card' && !!paymentMethod.token) && (<RemovePrompt token={paymentMethod.token} style={{ cursor: 'pointer', pointerEvents: 'auto' }} />)}
                    </>)}
                    {(type === 'add') && (<>
                        <div></div>
                        <div className="flex flex-row items-center justify-between text-gray-500" style={{ gap: 'inherit' }}>
                            <Icons.Plus style={{ marginRight: '0.25rem' }} />
                            <p style={{ flex: 1 }}>Add New Payment Method</p>
                        </div>
                        <div></div>
                    </>)}
                </div>
            )}
            {children}
        </div>
    )
}

// When I can come up with a good solution for a dialog component, I'll replace the one used here.
const RemovePrompt: FC<React.HTMLAttributes<HTMLDialogElement> & { token: string }> = (props) => {
    const { className: classProp, token, ...dialogProps } = props
    const className = flatten('pm-remove-modal', classProp)
    const ref = useRef<HTMLDialogElement>(null)
    const { revalidate, state } = useRevalidator()
    const { user } = useUser()

    const [isOpen, setIsOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        if (ref.current) {
            if (isOpen) ref.current.showModal();
            else ref.current.close()
        }
    }, [isOpen])

    // Since removing the payment method isn't done through an action, the global loader doesn't appear.
    // Revalidating to show an updated list takes time, and since we want to show some sort of loading indecator,
    // we cheat a little and show a loading indicator in the same dialog while the remove endpoint 
    // is being called and/or the revalidator is still revalidating.
    useEffect(() => {
        if (state === 'idle' && isLoading) {
            toggleDialog('closed')
            setIsLoading(false)
        }
    }, [state])

    function toggleDialog(open: 'open' | 'closed' = isOpen ? 'closed' : 'open') {
        setIsOpen(open === 'open')
    }

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault()
        setIsLoading(true)
        if (!isNonEmptyString(token) || !isNonEmptyString(user?.id)) throw createResponseObj({
            status: 'failed', type: '', name: '',
            message: `Invalid Form Data: ${JSON.stringify({ token, userId: user?.id })}`
        })

        await StripeAPI.RemovePaymentMethodRequest.fetch(token)
        queryClient.invalidateQueries({ queryKey: ['payment-methods'] })
        revalidate()
    }

    function handlePrompt(e: React.MouseEvent<SVGSVGElement, MouseEvent>) {
        e.stopPropagation()
        toggleDialog()
    }

    return (<>
        <Icons.Trash onClick={handlePrompt} style={{ cursor: 'pointer', pointerEvents: 'auto' }} />
        <dialog ref={ref} className={className} onClick={(e) => e.stopPropagation()} onClose={() => { if (isOpen) setIsOpen(false) }} {...dialogProps}>
            <div>
                {(isLoading || state === 'loading') ? (
                    <div className="loader flex flex-col items-center justify-center">
                        <Spinner />
                    </div>
                ) : (
                    <form className="flex flex-col items-stretch" onSubmit={handleSubmit}>
                        <h2 style={{ textAlign: 'center' }}>Wait!</h2>
                        <p style={{ textAlign: 'center' }}>Are you sure you want to remove this payment method?</p>
                        <div className='flex flex-row gap-2.5'>
                            <Button theme="secondary" style={{ flex: 1, cursor: 'pointer', pointerEvents: 'auto' }} onClick={() => toggleDialog()}>Cancel</Button>
                            <Button theme="primary" style={{ flex: 1, cursor: 'pointer', pointerEvents: 'auto' }} type="submit">Remove Payment</Button>
                        </div>
                    </form>
                )}
            </div>
        </dialog>
    </>)
}

export default PaymentMethodCell
